import dayjs, { Dayjs } from 'dayjs';
import { Listing, ListingPositionEnum } from '@/lib/types/api/multi-livestream';

// This function is the heart of the multilivestream listing logic.
// This looks at a listing, compares it to the schedule window,
// and returns a ListingPositionEnum value indicating where the listing
// is relative to the schedule window.
const getListingPosition = (
  listing: Listing,
  now: Dayjs,
  scheduleStart: Dayjs,
  scheduleEnd: Dayjs
): ListingPositionEnum => {
  // Time consts
  const listingStart = dayjs(listing.start_time);
  const listingEnd = listingStart.add(listing.duration, 'second');

  // Comparison consts
  const startsBeforeScheduleStart = listingStart.isBefore(
    scheduleStart,
    'minute'
  );
  const startsAtScheduleStart = listingStart.isSame(scheduleStart, 'minute');
  const isAiringNow = now.isAfter(listingStart) && now.isBefore(listingEnd);
  const startsAfterScheduleStart = listingStart.isAfter(
    scheduleStart,
    'minute'
  );
  const startsAfterScheduleEnd =
    listingStart.isAfter(scheduleEnd, 'minute') ||
    listingStart.isSame(scheduleEnd, 'minute');

  const endsBeforeScheduleStart =
    listingEnd.isBefore(scheduleStart, 'minute') ||
    listingEnd.isSame(scheduleStart, 'minute');
  const endsBeforeScheduleEnd = listingEnd.isBefore(scheduleEnd, 'minute');
  const endsAtScheduleEnd = listingEnd.isSame(scheduleEnd, 'minute');
  const endsAfterScheduleEnd = listingEnd.isAfter(scheduleEnd, 'minute');
  const scheduleIsForToday = scheduleStart.isSame(dayjs(now), 'day');

  switch (true) {
    case startsBeforeScheduleStart:
      if (endsBeforeScheduleStart) {
        // theoretically this shouldn't happen, but we need to throw out
        // these listings too if they do appear
        return ListingPositionEnum.startsBeforeScheduleStartAndEnded;
      } else if (!endsBeforeScheduleStart) {
        if (endsBeforeScheduleEnd) {
          return ListingPositionEnum.startsBeforeScheduleStartButNotEnded;
        } else if (endsAtScheduleEnd) {
          return ListingPositionEnum.startsBeforeScheduleStartEndsAtScheduleEnd;
        } else if (endsAfterScheduleEnd) {
          return ListingPositionEnum.listingLongerThanSchedule;
        }
      }
      break;
    case startsAtScheduleStart:
      if (!isAiringNow && scheduleIsForToday) {
        // Rare case, but it happens. A short listing starts at the start
        // of the current schedule window, but is over. This only matters for today.
        return ListingPositionEnum.startsAtScheduleStartAndIsEnded;
      } else if (endsBeforeScheduleEnd) {
        return ListingPositionEnum.startsAtScheduleStart;
      } else if (endsAtScheduleEnd) {
        return ListingPositionEnum.listingSameAsSchedule;
      } else if (endsAfterScheduleEnd) {
        return ListingPositionEnum.startsAtScheduleStartEndsAfterScheduleEnd;
      }
      break;
    case startsAfterScheduleStart:
      if (startsAfterScheduleEnd) {
        // this is the majority use case - most listings will
        // come after the schedule window
        return ListingPositionEnum.startsAfterScheduleEnd;
      } else if (endsBeforeScheduleEnd || endsAtScheduleEnd) {
        return ListingPositionEnum.duringSchedule;
      } else if (endsAfterScheduleEnd) {
        return ListingPositionEnum.duringScheduleEndsAfterScheduleEnd;
      }
      break;
  }

  // not sure how you'd get into this situation, but let's
  // cover our bases
  return ListingPositionEnum.notInSchedule;
};

export { getListingPosition };
